import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent } from '@theme/events';

/**
 * @typedef {object} FancyboxItem
 * @property {number} mediaIndex
 * @property {string} src
 * @property {string} thumb
 * @property {string} caption
 */

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a variant update event by replacing the current media gallery with a new one.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    const source = event.detail.data.html;

    if (!source) return;
    const newMediaGallery = source.querySelector('media-gallery');

    if (!newMediaGallery) return;

    this.replaceWith(newMediaGallery);
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    /** @type {FancyboxItem[]} */
    const fancyboxItems = this.#getFancyboxItems();
    const fancyboxStartIndex = fancyboxItems.findIndex((item) => item.mediaIndex === index);
    const fancybox = /** @type {any} */ (window).Fancybox;

    if (fancybox && fancyboxStartIndex !== -1) {
      event.preventDefault();
      fancybox.show(
        fancyboxItems.map((item) => ({
          src: item.src,
          type: 'image',
          thumb: item.thumb,
          caption: item.caption,
        })),
        {
          startIndex: fancyboxStartIndex,
        }
      );

      return;
    }

    this.refs.zoomDialogComponent?.open(index, event);
  }

  #getFancyboxItems() {
    const sourceMedia = this.refs.zoomDialogComponent?.refs.media;
    if (!sourceMedia?.length) return [];

    return sourceMedia.reduce(
      /** @param {FancyboxItem[]} items */
      (items, mediaContainer, mediaIndex) => {
      if (!mediaContainer.classList.contains('product-media-container--image')) return items;

      const image = mediaContainer.querySelector('img.product-media__image');
      if (!(image instanceof HTMLImageElement)) return items;

      const src = image.getAttribute('data_max_resolution') || image.currentSrc || image.src;
      if (!src) return items;

      items.push({
        mediaIndex,
        src,
        thumb: image.currentSrc || image.src,
        caption: image.alt,
      });

      return items;
      },
      /** @type {FancyboxItem[]} */ ([])
    );
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
